/**
 * TransactionStateIndicator — shared component showing transaction lifecycle status
 *
 * Renders current TransactionStatus with icon, title, description, and optional action.
 * Used by deploy (Epic 2), swap (Epic 5), and fee claims (Epic 6).
 *
 * UX-9: New component
 * UX-5: Framer Motion transitions
 */

import type { TransactionStatus } from "@repo/shared-types";
import { AnimatePresence, type Variants, motion } from "framer-motion";

/** Optional label overrides for context-specific copy (swap, fee claim, etc.) */
export type TransactionLabels = Partial<{
	idleTitle: string;
	idleDescription: string;
	confirmedTitle: string;
	confirmedDescription: string;
	actionButtonLabel: string;
}>;

type TransactionStateIndicatorProps = {
	status: TransactionStatus;
	txHash?: `0x${string}` | undefined;
	error?: string | undefined;
	blockExplorerUrl?: string | undefined;
	onRetry?: (() => void) | undefined;
	onDeploy?: (() => void) | undefined;
	labels?: TransactionLabels | undefined;
};

type StateConfig = {
	icon: string;
	title: string;
	description: string;
	showSpinner: boolean;
};

function getStateConfig(
	status: TransactionStatus,
	error: string | undefined,
	txHash: `0x${string}` | undefined,
	labels?: TransactionLabels
): StateConfig {
	switch (status) {
		case "idle":
			return {
				icon: "🚀",
				title: labels?.idleTitle ?? "Ready to Deploy",
				description:
					labels?.idleDescription ?? "Review your configuration and click Deploy to begin.",
				showSpinner: false,
			};
		case "preparing":
			return {
				icon: "⚙️",
				title: "Preparing Transaction",
				description: "Building transaction parameters...",
				showSpinner: true,
			};
		case "awaiting_signature":
			return {
				icon: "🔐",
				title: "Confirm in Wallet",
				description: "Please confirm the transaction in your wallet.",
				showSpinner: true,
			};
		case "pending":
			return {
				icon: "⏳",
				title: "Confirming on Chain",
				description: txHash
					? "Transaction submitted. Waiting for confirmation..."
					: "Waiting for confirmation...",
				showSpinner: true,
			};
		case "confirmed":
			return {
				icon: "✅",
				title: labels?.confirmedTitle ?? "Transaction Confirmed",
				description: labels?.confirmedDescription ?? "Your token has been successfully deployed!",
				showSpinner: false,
			};
		case "failed":
			return {
				icon: "❌",
				title: "Transaction Failed",
				description: error ?? "An unexpected error occurred. Please try again.",
				showSpinner: false,
			};
		case "rejected":
			return {
				icon: "ℹ️",
				title: "Transaction Cancelled",
				description:
					"You cancelled the transaction. Your data is preserved \u2014 you can try again.",
				showSpinner: false,
			};
	}
}

const variants: Variants = {
	enter: { opacity: 0, y: 10 },
	center: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
	exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

export function TransactionStateIndicator({
	status,
	txHash,
	error,
	blockExplorerUrl,
	onRetry,
	onDeploy,
	labels,
}: TransactionStateIndicatorProps) {
	const config = getStateConfig(status, error, txHash, labels);

	const explorerLink = txHash && blockExplorerUrl ? `${blockExplorerUrl}/tx/${txHash}` : undefined;

	return (
		<AnimatePresence mode="wait">
			<motion.div
				key={status}
				variants={variants}
				initial="enter"
				animate="center"
				exit="exit"
				className="rounded-xl border border-pastel-border bg-white p-6 text-center"
			>
				{/* Icon */}
				<div className="text-4xl mb-3" role="img" aria-label={config.title}>
					{config.icon}
				</div>

				{/* Spinner */}
				{config.showSpinner && (
					<div className="flex justify-center mb-3">
						<div className="h-6 w-6 animate-spin rounded-full border-2 border-pastel-vibrant-blue border-t-transparent" />
					</div>
				)}

				{/* Title */}
				<h3 className="text-lg font-semibold text-pastel-black mb-1">{config.title}</h3>

				{/* Description */}
				<p className="text-sm text-pastel-gray mb-4">{config.description}</p>

				{/* Explorer link */}
				{explorerLink && status === "pending" && (
					<a
						href={explorerLink}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm text-pastel-vibrant-blue hover:underline mb-4 inline-block"
					>
						View on Explorer ↗
					</a>
				)}

				{/* Action buttons */}
				{status === "idle" && onDeploy && (
					<button
						type="button"
						onClick={onDeploy}
						className="mt-2 rounded-lg bg-pastel-vibrant-blue px-6 py-2.5 text-sm font-semibold text-white hover:bg-pastel-vibrant-blue/90 transition-colors"
					>
						{labels?.actionButtonLabel ?? "Deploy Token"}
					</button>
				)}

				{(status === "failed" || status === "rejected") && onRetry && (
					<button
						type="button"
						onClick={onRetry}
						className="mt-2 rounded-lg bg-pastel-vibrant-blue px-6 py-2.5 text-sm font-semibold text-white hover:bg-pastel-vibrant-blue/90 transition-colors"
					>
						{status === "rejected" ? "Try Again" : "Retry"}
					</button>
				)}
			</motion.div>
		</AnimatePresence>
	);
}
